import type { IItem } from './types'

export function getAccountId(): string {
  // Check URL parameters first
  const urlParams = new URLSearchParams(window.location.search)
  const accountIdFromUrl = urlParams.get('account_id')
  if (accountIdFromUrl) return accountIdFromUrl

  // Check DOM attributes
  const wpElement = document.querySelector('.jellybeans')
  const accountIdFromElement = wpElement?.getAttribute('data-account-id')
  if (accountIdFromElement) return accountIdFromElement

  return ''
}

export function getPlacementId(): string {
  // Check URL parameters first
  const urlParams = new URLSearchParams(window.location.search)
  const accountIdFromUrl = urlParams.get('placement_id')
  if (accountIdFromUrl) return accountIdFromUrl

  // Check DOM attributes
  const wpElement = document.querySelector('.jellybeans')
  const accountIdFromElement = wpElement?.getAttribute('data-placement-id')
  if (accountIdFromElement) return accountIdFromElement

  return 'default'
}

/**
 * Evaluates a conditional expression within a template
 * @param expression The expression to evaluate
 * @param data The data object containing values
 * @returns The result of the evaluated expression
 */
const evaluateCondition = (expression: string, data: any): string => {
  try {
    // Match pattern: ({condition}) ? "trueValue" : "falseValue"
    const conditionRegex = /^\(\{(.*?)\}\)\s*\?\s*"([^"]*)"\s*:\s*"([^"]*)"$/
    const match = expression.trim().match(conditionRegex)

    if (!match) {
      return expression
    }

    const [, condition, trueValue, falseValue] = match

    if (data?.[condition] == null) {
      return ''
    }

    // Create a safe evaluation context
    const evalContext = { ...data }

    // Evaluate the condition
    const result = new Function('data', `with(data) { return ${condition} }`)(
      evalContext,
    )

    // Return the appropriate value based on condition
    return result ? trueValue : falseValue
  } catch (error) {
    console.error('### Error evaluating condition:', error)
    return expression
  }
}

/**
 * Processes a template string with provided data
 * @param template The template string to process
 * @param data The vehicle data item
 * @returns Processed string with replaced variables
 */
export const processTemplate = (
  template: string,
  data: { [key: string]: any },
): string => {
  let result = template
  // Process conditional expressions repeatedly until no more changes
  let previousResult
  do {
    previousResult = result
    // Handle conditional expressions
    result = result.replace(
      /\{(\(\{[^}]+\}\)\s*\?\s*"[^"]*"\s*:\s*"[^"]*")\}/g,
      (match, expression) => {
        return evaluateCondition(expression, data)
      },
    )
  } while (result !== previousResult) // Continue until no more replacements are made

  // Handle simple variable replacements
  result = result.replace(/\{\{([^}]+)\}\}/g, (match, key) => {
    const trimmedKey = key.trim()
    return (data?.[trimmedKey] ?? '').toString()
  })

  return result
}

/**
 * Processes multiple templates for a vehicle item
 * @param templates Array of template strings
 * @param item Vehicle data item
 * @returns Array of processed strings
 */
export const processTemplates = (
  templates: string[],
  item: IItem,
): string[] => {
  return templates.map(template => processTemplate(template, item.fields))
}
